import { motion, useReducedMotion } from "framer-motion";
import { Sparkle, ArrowRight, Users, LockSimple, Trophy, Ear } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { LumiTwirl } from "@/components/LumiTwirl";

const PERKS = [
  { icon: Ear, label: "Hear every word" },
  { icon: Trophy, label: "Earn private badges" },
  { icon: LockSimple, label: "No ads · kid-safe" },
];

const CHIPS = [
  { t: "Match the Meaning", pos: "top-6 -left-4 sm:-left-10" },
  { t: "Sentence Builder", pos: "top-[46%] -right-4 sm:-right-12" },
  { t: "Story Magic", pos: "bottom-8 -left-2 sm:-left-8" },
];

export const KidsHero = () => {
  const reduce = useReducedMotion();
  return (
  <section
    id="kids-hero"
    className="relative overflow-hidden text-white"
    style={{ background: "linear-gradient(135deg, #0f285f 0%, #15367a 45%, #7358b3 100%)" }}
    aria-labelledby="hero-title"
  >
    <div className="absolute inset-0 bg-[radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px] pointer-events-none" />
    <div className="absolute -top-32 right-0 w-[320px] h-[320px] sm:w-[480px] sm:h-[480px] rounded-full bg-[#ff8518]/20 blur-3xl pointer-events-none translate-x-1/4" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
        <motion.div
          className="lg:col-span-7 text-center lg:text-left"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <span className="inline-flex items-center gap-2 bg-white/10 border border-white/25 rounded-full px-4 py-1.5 mb-5 backdrop-blur-sm">
            <Sparkle size={16} weight="fill" className="text-[#ffb066]" />
            <span className="font-nunito font-bold text-xs sm:text-sm text-white uppercase tracking-[0.18em]">Kids Corner with Lumi</span>
          </span>
          <h1
            id="hero-title"
            className="font-nunito font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight mb-5"
            data-testid="hero-title"
          >
            Where words become <span className="text-[#ffb066]">an adventure.</span>
          </h1>
          <p className="font-nunito text-base sm:text-lg lg:text-xl text-white/85 leading-relaxed max-w-xl mx-auto lg:mx-0 mb-8">
            Children build vocabulary and confidence through playful, guided activities — with private progress,
            encouraging feedback, and Lumi the Word Fairy cheering them on every step of the way.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
            <a href="/kids-corner" data-testid="hero-start-playing-btn">
              <Button className="w-full sm:w-auto bg-[#ff8518] hover:bg-[#e8740f] text-white font-nunito font-extrabold text-lg rounded-full px-8 py-6 shadow-[0_10px_30px_rgba(255,133,24,0.45)] border-0">
                <Sparkle size={22} weight="fill" className="mr-2" />
                Start Playing
                <ArrowRight size={22} weight="bold" className="ml-2" />
              </Button>
            </a>
            <a href="#resources" data-testid="hero-resources-btn">
              <Button variant="outline" className="w-full sm:w-auto bg-white text-[#0f285f] hover:bg-white/90 font-nunito font-bold text-base rounded-full px-7 py-6 border-2 border-white">
                <Users size={22} weight="bold" className="mr-2" />
                Parent &amp; Teacher Resources
              </Button>
            </a>
          </div>
          <ul className="flex flex-wrap gap-x-6 gap-y-2 justify-center lg:justify-start mt-8" aria-label="Kids Corner highlights">
            {PERKS.map((p) => (
              <li key={p.label} className="flex items-center gap-2 font-nunito font-semibold text-sm text-white/80">
                <p.icon size={18} weight="bold" className="text-[#ffb066]" />
                {p.label}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          className="lg:col-span-5 relative flex justify-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          <div className="relative w-[230px] sm:w-[280px] lg:w-[320px]">
            <div className={`absolute -inset-10 rounded-full bg-[radial-gradient(circle,rgba(255,133,24,0.5)_0%,rgba(255,176,102,0.22)_45%,transparent_70%)] blur-2xl pointer-events-none ${reduce ? "" : "animate-pulse"}`} />
            <LumiTwirl className="relative border-4 border-white/70 shadow-[0_24px_60px_rgba(0,0,0,0.35)]" testid="hero-lumi-twirl" />
            {CHIPS.map((chip, i) => (
              <motion.span
                key={chip.t}
                className={`absolute ${chip.pos} bg-white text-[#0f285f] font-nunito font-bold text-xs sm:text-sm rounded-full px-3 py-1.5 shadow-lg`}
                initial={{ opacity: 0, scale: 0.6 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + i * 0.15 }}
              >
                ✨ {chip.t}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  </section>
  );
};
